/**
 * One Flag, opened from the Notification Center (Traci's review, 23 Sep 2026; prototype v108 §8.4): the original issue,
 * who it is assigned to, the Discussion thread with @mentions, and Resolve for Admin or the owning department. A
 * mention notifies that person and lets them read and reply to this flag only.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { Button } from '@/components/admin/ui/button';
import { Textarea } from '@/components/admin/ui/textarea';
import { mentionQuery, matchStaff, insertMention } from '@/lib/notifications/mentions';
import { ROLE_LABELS as DEPT } from '@/lib/notifications/center';
import { STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch } from '../api';

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' }) : '');

export default function FlagDialog({ flagId, onClose, onChanged }: { flagId: string | null; onClose: () => void; onChanged?: () => void }) {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staff, setStaff] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [resolution, setResolution] = useState('');
  const [busy, setBusy] = useState(false);
  const [menuIndex, setMenuIndex] = useState(0);
  const box = useRef<HTMLTextAreaElement | null>(null);

  const load = () => flagId && stagingFetch(`/api/admin/staging/flags/${flagId}`).then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message || String(e)));
  useEffect(() => { setData(null); setText(''); setResolution(''); if (flagId) load(); }, [flagId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (flagId && !staff.length) stagingFetch('/api/admin/staging/staff').then((d) => setStaff(d.staff || [])).catch(() => {}); }, [flagId, staff.length]);

  const query = mentionQuery(text, caret);
  const options = useMemo(() => (query === null ? [] : matchStaff(staff, query)), [query, staff]);
  const pick = (p: any) => { const r = insertMention(text, caret, p); setText(r.text); setCaret(r.caret); setMenuIndex(0); setTimeout(() => { box.current?.focus(); box.current?.setSelectionRange(r.caret, r.caret); }, 0); };

  const send = async () => {
    setBusy(true);
    try {
      const r = await stagingFetch(`/api/admin/staging/flags/${flagId}/comments`, { method: 'POST', body: JSON.stringify({ body: text.trim() }) });
      toast.success(r.mentioned?.length ? `Sent · ${r.mentioned.join(', ')} notified` : 'Sent');
      setText(''); await load(); onChanged?.();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setBusy(false); }
  };
  const resolve = async () => {
    setBusy(true);
    try {
      await stagingFetch(`/api/admin/staging/flags/${flagId}/resolve`, { method: 'POST', body: JSON.stringify({ note: resolution }) });
      toast.success('Flag resolved. It stays on record as resolved.');
      setResolution(''); await load(); onChanged?.();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setBusy(false); }
  };

  const f = data?.flag;
  const resolved = f?.status === 'resolved';
  return (
    <Dialog open={!!flagId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        {!data && !error && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {error && <p className="text-sm text-destructive">{error}</p>}
        {f && (
          <>
            <DialogHeader>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{DEPT[f.assigned_to_role] || f.assigned_to_role || 'Unassigned'} Flag</div>
              <DialogTitle className="flex flex-wrap items-center gap-2">
                {f.user_id ? <Link className="hover:underline" to={`${STAGING_ROOT}/clients/${f.user_id}`} onClick={onClose}>{data.names.client || 'Client'}</Link> : 'Flag'}
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${resolved ? 'border-emerald-300 text-emerald-800' : f.status === 'escalated' ? 'border-red-300 text-red-800' : 'border-amber-300 text-amber-800'}`}>{resolved ? 'Resolved' : f.status === 'escalated' ? 'Escalated' : 'Active'}</span>
              </DialogTitle>
              <DialogDescription>Raised {when(f.created_at)}{data.names.flagged_by ? ` by ${data.names.flagged_by}` : ''}</DialogDescription>
            </DialogHeader>

            <div className="rounded-md border bg-muted/30 p-3 text-sm">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Original issue</div>
              <p className="mt-1 whitespace-pre-wrap">{f.reason}</p>
              {f.notes && <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{f.notes}</p>}
              <p className="mt-2 text-xs">Assigned to: <b>{data.names.assigned_to || 'Unassigned person'}</b>{f.assigned_to_role ? ` · ${DEPT[f.assigned_to_role] || f.assigned_to_role}` : ''}</p>
              {resolved && <p className="mt-1 text-xs text-emerald-800">Resolved {when(f.resolved_at)}</p>}
            </div>

            <div>
              <div className="text-sm font-semibold">Discussion</div>
              <p className="text-xs text-muted-foreground">Internal follow-up. @mentions notify the person but do not change who owns the flag.</p>
              {!data.installed && <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">The discussion thread needs the flag_comments migration. Until it runs, the flag can still be resolved; the resolution note is kept with the flag.</p>}
              <div className="mt-2 max-h-[260px] space-y-2 overflow-y-auto">
                {data.installed && data.comments.length === 0 && <p className="text-xs text-muted-foreground">No messages yet.</p>}
                {data.comments.map((c: any) => (
                  <div key={c.id} className={`rounded-md border p-2.5 text-sm ${c.kind === 'resolution' ? 'border-emerald-200 bg-emerald-50' : ''}`}>
                    <p className="whitespace-pre-wrap">{c.kind === 'resolution' ? `Resolved: ${c.body}` : c.body}</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">{c.author_name || 'Staff'} · {when(c.created_at)}</p>
                  </div>
                ))}
              </div>
              {data.installed && data.can_comment && (
                <div className="relative mt-2">
                  <Textarea ref={box} rows={3} value={text} placeholder="Write a message… Type @ and the first letters of a name to mention someone." aria-label="Message"
                    onChange={(e) => { setText(e.target.value); setCaret(e.target.selectionStart || 0); setMenuIndex(0); }}
                    onKeyUp={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart || 0)}
                    onKeyDown={(e) => {
                      if (!options.length) return;
                      if (e.key === 'ArrowDown') { e.preventDefault(); setMenuIndex((i) => (i + 1) % options.length); }
                      if (e.key === 'ArrowUp') { e.preventDefault(); setMenuIndex((i) => (i - 1 + options.length) % options.length); }
                      if (e.key === 'Enter') { e.preventDefault(); pick(options[menuIndex]); }
                      if (e.key === 'Escape') { e.preventDefault(); setCaret(0); }
                    }} />
                  {options.length > 0 && (
                    <ul role="listbox" aria-label="Mention someone" className="absolute left-0 right-0 z-20 mt-1 max-h-48 overflow-y-auto rounded-md border bg-popover shadow">
                      {options.map((p, i) => (
                        <li key={p.id} role="option" aria-selected={i === menuIndex}>
                          <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }} className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${i === menuIndex ? 'bg-accent' : ''}`}>
                            <b className="font-medium">{p.name}</b><span className="text-[11px] text-muted-foreground">{p.roles.map((r: string) => DEPT[r] || r).join(', ')}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-2 flex justify-end"><Button size="sm" onClick={send} disabled={busy || !text.trim()}>Send</Button></div>
                </div>
              )}
            </div>

            {data.can_resolve && !resolved && (
              <div className="rounded-md border p-3">
                <div className="text-sm font-semibold">Resolve Flag</div>
                <p className="text-xs text-muted-foreground">Admin or anyone in the department that owns this flag can resolve it. It stays on record as resolved.</p>
                <Textarea className="mt-2" rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Resolution note (optional)" aria-label="Resolution note" />
                <div className="mt-2 flex justify-end"><Button size="sm" variant="outline" onClick={resolve} disabled={busy}>Mark as Resolved</Button></div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
